import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { thumb, formatApiError } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Minus, Plus, Trash2, Store, Truck, ShoppingBag, ArrowRight } from "lucide-react";
import CouponField from "../components/CouponField";
import { trackInitiateCheckout } from "../lib/metaPixel";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const loggedIn = user && user !== false;

  const allShip = items.length > 0 && items.every((i) => i.shipping);
  const allPickup = items.length > 0 && items.every((i) => i.pickup);
  const [fulfillment, setFulfillment] = useState(allPickup ? "pickup" : "ship");
  const [guest, setGuest] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [coupon, setCoupon] = useState(null);
  const [giftInput, setGiftInput] = useState("");
  const [gift, setGift] = useState(null);
  const applyGift = async () => {
    const code = giftInput.trim().toUpperCase();
    if (!code) return;
    try {
      const { data } = await api.get(`/gift/lookup/${encodeURIComponent(code)}`);
      if (!(data.balance > 0)) return toast.error("That gift card has no balance left");
      setGift(data); toast.success(`Gift card applied — $${data.balance.toFixed(2)} available`);
    } catch { toast.error("Invalid gift card code"); }
  };
  const [membership, setMembership] = useState(null);

  useEffect(() => {
    if (loggedIn) api.get("/membership/me").then(({ data }) => setMembership(data)).catch(() => setMembership(null));
    else setMembership(null);
  }, [loggedIn]);

  const shipFee = fulfillment === "ship" ? items.reduce((m, i) => Math.max(m, i.shipping_fee || 0), 0) : 0;
  const memberActive = !!membership?.active;
  const memberPct = (membership?.discount_percent ?? 15) / 100;
  const memberSubtotal = memberActive ? Math.round(subtotal * (1 - memberPct) * 100) / 100 : subtotal;
  const memberSavings = Math.round((subtotal - memberSubtotal) * 100) / 100;
  const couponDiscount = coupon
    ? (coupon.discount_type === "percent"
        ? Math.round(memberSubtotal * (coupon.discount_value / 100) * 100) / 100
        : Math.min(coupon.discount_value, memberSubtotal))
    : 0;
  const preGift = Math.max(0, Math.round((memberSubtotal - couponDiscount) * 100) / 100) + shipFee;
  const giftApplied = gift ? Math.min(gift.balance, preGift) : 0;
  const total = Math.max(0, Math.round((preGift - giftApplied) * 100) / 100);

  const checkout = async () => {
    if (items.length === 0) return;
    if (!loggedIn && (!guest.name || !guest.email)) {
      toast.error("Please add your name and email to check out as a guest");
      return;
    }
    if (fulfillment === "ship" && !allShip) {
      toast.error("Some items are pickup only — choose studio pickup or remove them");
      return;
    }
    setBusy(true);
    trackInitiateCheckout({ value: total, numItems: items.reduce((n, i) => n + i.qty, 0), name: "Shop order" });
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, qty: i.qty, size: i.size })),
        fulfillment,
        origin_url: window.location.origin,
      };
      if (!loggedIn) { payload.guest_name = guest.name; payload.guest_email = guest.email; }
      if (coupon?.code) payload.coupon_code = coupon.code;
      if (gift?.code) payload.gift_code = gift.code;
      const { data } = await api.post("/shop/checkout", payload);
      clear();
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  if (items.length === 0) {
    return (
      <div data-testid="cart-empty" className="pt-40 pb-32 fade-in text-center">
        <ShoppingBag className="mx-auto text-[#121212]" size={48} />
        <h1 className="mt-6 font-serif font-black text-4xl text-[#121212]">Your cart is empty.</h1>
        <p className="mt-3 text-stone-600">Take the studio home with a kit or some merch.</p>
        <Link to="/shop" data-testid="cart-shop-link" className="mt-8 inline-flex items-center gap-2 px-8 py-4 bg-[#121212] text-[#F2E9D8] text-xs tracking-[0.2em] uppercase font-bold hover:bg-black transition-colors">
          Browse the shop <ArrowRight size={15} />
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="pt-32 pb-24 fade-in">
      <div className="max-w-5xl mx-auto px-6 sm:px-12 lg:px-16">
        <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#121212] mb-4">Your bag</p>
        <h1 className="font-serif font-black text-5xl text-[#121212]">Checkout.</h1>

        <div className="mt-12 grid lg:grid-cols-3 gap-12">
          {/* Items */}
          <div className="lg:col-span-2 space-y-4">
            {items.map((i) => (
              <div key={i.key} data-testid={`cart-item-${i.product_id}`} className="flex gap-4 bg-white border-2 border-[#121212] p-4">
                {i.image_url && <img src={thumb(i.image_url, 200)} alt={i.title} className="w-24 h-24 object-cover border border-[#121212]/20" />}
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between gap-3">
                    <div>
                      <p className="font-serif font-black text-lg text-[#121212] leading-tight">{i.title}</p>
                      {i.size && <p className="text-xs tracking-[0.15em] uppercase text-stone-500 mt-0.5">Size: {i.size}</p>}
                      {i.category === "cake_kit" && <p className="text-xs text-[#121212]/60 mt-0.5">Includes a Vault tutorial access code</p>}
                    </div>
                    <span className="font-serif font-black text-lg text-[#121212] whitespace-nowrap">${((Number(i.price) || 0) * i.qty).toFixed(2)}</span>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="inline-flex items-center border-2 border-[#121212]">
                      <button data-testid={`cart-dec-${i.product_id}`} onClick={() => updateQty(i.key, i.qty - 1)} className="px-2.5 py-1.5 hover:bg-[#F2E9D8]"><Minus size={14} /></button>
                      <span data-testid={`cart-qty-${i.product_id}`} className="px-4 text-sm font-bold">{i.qty}</span>
                      <button data-testid={`cart-inc-${i.product_id}`} onClick={() => updateQty(i.key, i.qty + 1)} className="px-2.5 py-1.5 hover:bg-[#F2E9D8]"><Plus size={14} /></button>
                    </div>
                    <button data-testid={`cart-remove-${i.product_id}`} onClick={() => removeItem(i.key)} className="text-stone-400 hover:text-[#E0218A] inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] font-bold">
                      <Trash2 size={14} /> Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <div className="bg-[#121212] text-[#F2E9D8] p-6 sticky top-28">
              <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#C6FF33] mb-4">Order summary</p>

              {/* Fulfillment */}
              <p className="text-[11px] tracking-[0.2em] uppercase font-bold mb-2">Delivery</p>
              <div className="space-y-2 mb-5">
                {allPickup && (
                  <button data-testid="fulfillment-pickup" onClick={() => setFulfillment("pickup")} className={`w-full flex items-center gap-2 px-3 py-2.5 border-2 text-left text-sm ${fulfillment === "pickup" ? "border-[#C6FF33] bg-[#C6FF33] text-[#121212]" : "border-[#F2E9D8]/30"}`}>
                    <Store size={16} /> Studio pickup — free
                  </button>
                )}
                {allShip && (
                  <button data-testid="fulfillment-ship" onClick={() => setFulfillment("ship")} className={`w-full flex items-center gap-2 px-3 py-2.5 border-2 text-left text-sm ${fulfillment === "ship" ? "border-[#C6FF33] bg-[#C6FF33] text-[#121212]" : "border-[#F2E9D8]/30"}`}>
                    <Truck size={16} /> Ship to me{items.reduce((m, i) => Math.max(m, i.shipping_fee || 0), 0) ? ` — $${items.reduce((m, i) => Math.max(m, i.shipping_fee || 0), 0).toFixed(2)}` : ""}
                  </button>
                )}
                {!allPickup && !allShip && (
                  <p className="text-xs text-[#F2E9D8]/70">Mixed delivery options — please check out kits and shippable items separately.</p>
                )}
              </div>

              {/* Guest info */}
              {!loggedIn && (
                <div className="space-y-2 mb-5">
                  <p className="text-[11px] tracking-[0.2em] uppercase font-bold">Your details</p>
                  <input data-testid="cart-guest-name" value={guest.name} onChange={(e) => setGuest({ ...guest, name: e.target.value })} placeholder="Full name" className="w-full px-3 py-2.5 bg-transparent border-2 border-[#F2E9D8]/30 text-sm placeholder:text-[#F2E9D8]/40 focus:outline-none focus:border-[#C6FF33]" />
                  <input data-testid="cart-guest-email" type="email" value={guest.email} onChange={(e) => setGuest({ ...guest, email: e.target.value })} placeholder="Email" className="w-full px-3 py-2.5 bg-transparent border-2 border-[#F2E9D8]/30 text-sm placeholder:text-[#F2E9D8]/40 focus:outline-none focus:border-[#C6FF33]" />
                  <p data-testid="cart-member-nudge" className="text-[11px] text-[#F2E9D8]/60">
                    Member?{" "}
                    <button type="button" onClick={() => nav(`/login?next=${encodeURIComponent("/cart")}`)} className="font-bold underline text-[#C6FF33]">Sign in</button>
                    {" "}for 15% off.
                  </p>
                </div>
              )}

              <div className="space-y-1.5 text-sm border-t border-[#F2E9D8]/20 pt-4">
                <div className="mb-2"><CouponField amount={subtotal} applied={coupon} onChange={setCoupon} context="merch" testid="cart-coupon" /></div>
                {gift ? (
                  <div className="mb-2 flex items-center justify-between text-xs"><span className="font-mono">{gift.code}</span>
                    <button data-testid="cart-gift-remove" onClick={() => { setGift(null); setGiftInput(""); }} className="underline text-[#F2E9D8]/70">Remove</button></div>
                ) : (
                  <div className="mb-2 flex gap-2">
                    <input data-testid="cart-gift-input" value={giftInput} onChange={(e) => setGiftInput(e.target.value)} placeholder="Gift card code"
                      className="flex-1 min-w-0 px-3 py-2 bg-transparent border border-[#F2E9D8]/30 text-[#F2E9D8] placeholder-[#F2E9D8]/40 font-mono uppercase text-xs focus:outline-none focus:border-[#C6FF33]" />
                    <button data-testid="cart-gift-apply" onClick={applyGift} className="px-3 py-2 border border-[#C6FF33] text-[#C6FF33] text-[11px] tracking-[0.15em] uppercase font-bold">Apply</button>
                  </div>
                )}
                <div className="flex justify-between"><span className="text-[#F2E9D8]/70">Subtotal</span><span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span></div>
                {memberSavings > 0 && (
                  <div className="flex justify-between text-[#C6FF33]"><span>Member 15% off</span><span data-testid="cart-member-savings">−${memberSavings.toFixed(2)}</span></div>
                )}
                {couponDiscount > 0 && (
                  <div className="flex justify-between text-[#C6FF33]"><span>Coupon {coupon.code}</span><span data-testid="cart-discount">−${couponDiscount.toFixed(2)}</span></div>
                )}
                <div className="flex justify-between"><span className="text-[#F2E9D8]/70">Shipping</span><span data-testid="cart-shipping">{shipFee ? `$${shipFee.toFixed(2)}` : "Free"}</span></div>
                {giftApplied > 0 && (
                  <div className="flex justify-between text-[#C6FF33]"><span>Gift card</span><span data-testid="cart-gift-applied">−${giftApplied.toFixed(2)}</span></div>
                )}
                <div className="flex justify-between font-serif font-black text-xl pt-2"><span>Total</span><span data-testid="cart-total">${total.toFixed(2)}</span></div>
                <p className="text-[11px] text-[#F2E9D8]/50 pt-1">Taxes calculated at checkout.</p>
              </div>

              <button data-testid="cart-checkout-btn" onClick={checkout} disabled={busy} className="mt-5 w-full px-6 py-4 bg-[#C6FF33] text-[#121212] text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#b3ec1f] disabled:opacity-50 transition-colors">
                {busy ? "Redirecting…" : "Proceed to payment"}
              </button>
              <Link to="/shop" className="mt-3 block text-center text-[11px] tracking-[0.2em] uppercase text-[#F2E9D8]/70 hover:text-[#C6FF33]">Continue shopping</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
